import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { AustralianState } from "@/lib/availability";
import { BRANCHES } from "@/lib/branchAvailability";

const STORAGE_KEY = "st_selected_location";
const BRANCH_STORAGE_KEY = "st_selected_branch";
// Session-scoped flag — cleared when the browser session ends / browser restarts.
const SESSION_PROMPTED_KEY = "st_location_session_prompted";

interface LocationContextValue {
  /** Selected state code, or null if none chosen. */
  state: AustralianState | null;
  /** Persist a selected state. */
  setState: (state: AustralianState) => void;
  /** Selected pickup/showroom branch key (e.g. "virginia"), or null. */
  branch: string | null;
  /** Persist a selected branch key (null clears it). */
  setBranch: (branch: string | null) => void;
  /** Whether the selector should be shown. */
  showSelector: boolean;
  /** Open the location selector modal manually (e.g. "Change"). */
  openSelector: () => void;
  /** Close the selector without choosing (keeps any existing location). */
  closeSelector: () => void;
  /** "Skip for now" — clears the selected location and uses no-location mode. */
  skipLocation: () => void;
}

const LocationContext = createContext<LocationContextValue | undefined>(undefined);

function readStored(): AustralianState | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "QLD" || v === "NSW" || v === "VIC" || v === "SA") return v;
  } catch {
    /* ignore */
  }
  return null;
}

export function LocationProvider({ children }: { children: ReactNode }) {
  const [state, setStateValue] = useState<AustralianState | null>(null);
  const [branch, setBranchValue] = useState<string | null>(null);
  const [showSelector, setShowSelector] = useState(false);
  const queryClient = useQueryClient();

  // Initialise from localStorage on mount. The saved location persists across
  // sessions so pricing/availability keeps working, but the selector is shown
  // once per browser session (sessionStorage) so the visitor can confirm or
  // change their location after a new session / browser restart.
  useEffect(() => {
    const stored = readStored();
    if (stored) setStateValue(stored);
    try {
      const b = localStorage.getItem(BRANCH_STORAGE_KEY);
      if (b) setBranchValue(b);
    } catch {
      /* ignore */
    }

    let promptedThisSession = false;
    try {
      promptedThisSession = sessionStorage.getItem(SESSION_PROMPTED_KEY) === "1";
    } catch {
      /* ignore */
    }
    if (!promptedThisSession) setShowSelector(true);
  }, []);

  function markPrompted() {
    try {
      sessionStorage.setItem(SESSION_PROMPTED_KEY, "1");
    } catch {
      /* ignore */
    }
  }

  // Refetch every location-aware query so the whole site (prices, availability,
  // warehouse options, badges) updates immediately without a manual refresh.
  const invalidateLocationData = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["product-availability-batch"] });
    queryClient.invalidateQueries({ queryKey: ["product-availability-global-batch"] });
    queryClient.invalidateQueries({ queryKey: ["branch-availability-batch"] });
  }, [queryClient]);


  const setBranch = useCallback((next: string | null) => {
    setBranchValue(next);
    try {
      if (next) localStorage.setItem(BRANCH_STORAGE_KEY, next);
      else localStorage.removeItem(BRANCH_STORAGE_KEY);
    } catch {
      /* ignore */
    }
    invalidateLocationData();
  }, [invalidateLocationData]);

  const setState = useCallback((next: AustralianState) => {
    setStateValue(next);
    setShowSelector(false);
    markPrompted();
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
    // Clear a branch that belongs to a different state.
    setBranchValue((prev) => {
      if (prev && !BRANCHES.some((b) => b.key === prev && b.state === next)) {
        try {
          localStorage.removeItem(BRANCH_STORAGE_KEY);
        } catch {
          /* ignore */
        }
        return null;
      }
      return prev;
    });
    invalidateLocationData();
  }, [invalidateLocationData]);

  const openSelector = useCallback(() => setShowSelector(true), []);

  const closeSelector = useCallback(() => {
    setShowSelector(false);
    markPrompted();
  }, []);

  const skipLocation = useCallback(() => {
    setShowSelector(false);
    markPrompted();
    setStateValue(null);
    setBranchValue(null);
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(BRANCH_STORAGE_KEY);
    } catch {
      /* ignore */
    }
    invalidateLocationData();
  }, [invalidateLocationData]);

  return (
    <LocationContext.Provider
      value={{ state, setState, branch, setBranch, showSelector, openSelector, closeSelector, skipLocation }}
    >
      {children}
    </LocationContext.Provider>
  );
}

export function useLocationContext(): LocationContextValue {
  const ctx = useContext(LocationContext);
  if (!ctx) throw new Error("useLocationContext must be used within LocationProvider");
  return ctx;
}
